import React, { useState } from 'react';
import { StarIcon, PencilIcon, TrashIcon, ChatBubbleLeftIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { useBookReviews, BookReview } from '../hooks/useBookReviews';
import { useAuth } from '../contexts/AuthContext';
import ReviewModal from './ReviewModal';
import { formatDate } from '../utils/formatUtils';

interface BookReviewsProps {
  bookId: string;
  bookTitle: string;
}

const BookReviews: React.FC<BookReviewsProps> = ({ bookId, bookTitle }) => {
  const {
    reviews,
    loading,
    error,
    userReview,
    ratingStats,
    userOwnsBook,
    addReview,
    updateReview,
    deleteReview,
    canUserReview
  } = useBookReviews(bookId);

  const { currentUser } = useAuth();

  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleSubmitReview = async (rating: number, review: string) => {
    if (userReview) {
      // Update existing review
      await updateReview(userReview.id!, rating, review);
      setIsEditing(false);
    } else {
      // Add new review
      await addReview(bookId, rating, review);
    }
  };

  const handleEditReview = () => {
    setIsEditing(true);
    setIsReviewModalOpen(true);
  };

  const handleDeleteReview = async () => {
    if (userReview) {
      await deleteReview(userReview.id!);
      setShowDeleteConfirm(false);
    }
  };

  const renderStars = (rating: number, size: 'sm' | 'md' | 'lg' = 'md') => {
    const sizeClasses = {
      sm: 'h-4 w-4',
      md: 'h-5 w-5',
      lg: 'h-6 w-6'
    };

    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <span key={star}>
            {rating >= star ? (
              <StarIconSolid className={`${sizeClasses[size]} text-yellow-400`} />
            ) : (
              <StarIcon className={`${sizeClasses[size]} text-gray-300`} />
            )}
          </span>
        ))}
      </div>
    );
  };

  if (loading && reviews.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Error loading reviews: {error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Reviews Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <ChatBubbleLeftIcon className="h-6 w-6 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">
            Reviews ({ratingStats?.totalReviews || 0})
          </h3>
        </div>
        
        {canUserReview() && (
          <button
            onClick={() => setIsReviewModalOpen(true)}
            className="btn-primary px-4 py-2 text-sm"
          >
            Write a Review
          </button>
        )}

        {currentUser && !userOwnsBook && (
          <div className="text-sm text-gray-500 bg-gray-100 px-3 py-2 rounded-lg">
            Purchase this book to write a review
          </div>
        )}

        {!currentUser && (
          <div className="text-sm text-gray-500 bg-gray-100 px-3 py-2 rounded-lg">
            Sign in to write reviews
          </div>
        )}
      </div>

      {/* Rating Summary */}
      {ratingStats && ratingStats.totalReviews > 0 && (
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-4">
            {/* Average Rating */}
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900">
                {ratingStats.averageRating}
              </div>
              <div className="text-sm text-gray-600">out of 5</div>
              {renderStars(Math.round(ratingStats.averageRating), 'lg')}
              <div className="text-sm text-gray-500 mt-1">
                {ratingStats.totalReviews} review{ratingStats.totalReviews !== 1 ? 's' : ''}
              </div>
            </div>

            {/* Rating Distribution */}
            <div className="flex-1 space-y-2">
              {[5, 4, 3, 2, 1].map((star) => {
                const count = ratingStats.ratingDistribution[star as keyof typeof ratingStats.ratingDistribution];
                const percentage = ratingStats.totalReviews > 0 ? (count / ratingStats.totalReviews) * 100 : 0;
                
                return (
                  <div key={star} className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600 w-4">{star}</span>
                    <StarIconSolid className="h-4 w-4 text-yellow-400" />
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-yellow-400 h-2 rounded-full"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-600 w-8">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* User's Review */}
      {userReview && (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                <span className="font-medium text-primary-900">Your Review</span>
                {renderStars(userReview.rating)}
                <span className="text-sm text-primary-600">
                  {formatDate(userReview.createdAt)}
                </span>
              </div>
              <p className="text-primary-800">{userReview.review}</p>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleEditReview}
                className="p-2 text-primary-600 hover:bg-primary-100 rounded-lg transition-colors"
                title="Edit review"
              >
                <PencilIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                title="Delete review"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reviews List */}
      <div className="space-y-4">
        {reviews.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <ChatBubbleLeftIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No reviews yet. Be the first to review this book!</p>
          </div>
        ) : (
          reviews
            .filter(review => !userReview || review.id !== userReview.id) // Don't show user's review twice
            .map((review) => (
              <div key={review.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-gray-900">
                      {review.userDisplayName || 'Anonymous'}
                    </span>
                    {renderStars(review.rating)}
                  </div>
                  <span className="text-sm text-gray-500">
                    {formatDate(review.createdAt)}
                  </span>
                </div>
                <p className="text-gray-700">{review.review}</p>
              </div>
            ))
        )}
      </div>

      {/* Review Modal */}
      <ReviewModal
        isOpen={isReviewModalOpen}
        onClose={() => {
          setIsReviewModalOpen(false);
          setIsEditing(false);
        }}
        onSubmit={handleSubmitReview}
        bookTitle={bookTitle}
        existingReview={isEditing ? userReview : null}
        isLoading={loading}
      />

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowDeleteConfirm(false)} />
            
            <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <TrashIcon className="h-6 w-6 text-red-600" />
                </div>
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <h3 className="text-base font-semibold leading-6 text-gray-900">
                    Delete Review
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to delete your review? This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleDeleteReview}
                  className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                >
                  Delete
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookReviews;
